import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import { useBannerOffset } from "@hooks/useBannerOffset";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";

import { useAuth } from "@/hooks/auth";
import { useOpenReactivationCheckout } from "@/hooks/useOpenReactivationCheckout";

/**
 * Site-wide non-dismissible banner shown on every page (except the claims page
 * itself, which has a richer in-page banner + the re-check modal) when the
 * logged-in user's whitelisting is suspended because a re-check found the
 * required Credit Text removed from a released video (ToU §17.4).
 *
 * Mirrors PastDueGlobalBanner: fetches on auth + route change and publishes its
 * height to `--whitelisting-banner-h` for layout offset. The CTA depends on the
 * state: credit-missing sends the user to /account/claims to re-check each
 * affected video; awaiting-reactivation opens the prefilled checkout directly,
 * since by then there is nothing left to fix.
 */
const WhitelistingSuspendedGlobalBanner: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { open: openReactivation } = useOpenReactivationCheckout();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { myData } = useAuth();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [suspended, setSuspended] = useState(false);
  const [awaiting, setAwaiting] = useState(false);
  const [videoCount, setVideoCount] = useState(0);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!userId) {
      setSuspended(false);
      setAwaiting(false);
      return;
    }
    instance
      .get("/claims/my/whitelisting-suspension-info")
      .then(({ data }) => {
        setSuspended(!!data?.suspended);
        setAwaiting(!!data?.awaitingReactivation);
        setVideoCount((data?.videos ?? []).length);
      })
      .catch(() => { setSuspended(false); setAwaiting(false); });
  }, [userId, pathname]);

  // Shown on every page EXCEPT the claims page (which has its own in-page
  // banner + the re-check modal).
  const visible = suspended && !pathname.startsWith("/account/claims");

  // On /account/* pages the SecondHeader renders in normal flow (position:
  // static), so the banner inside it already reserves its own height. The global

  // Publish the banner height for the global layout offset. Keyed on `visible`
  // (not just `suspended`) and re-attaching the observer each time it shows:
  // routing onto /account/claims UNMOUNTS the banner, which fires the
  // ResizeObserver with height 0 and zeroes the var. Keying only on `suspended`
  // (unchanged across that route) meant we never re-measured the freshly-mounted
  // banner on the way back, so the reserved space stayed lost. Re-running on
  // `visible` re-observes the current element and restores the height.
  useBannerOffset("--whitelisting-banner-h", visible, bannerRef);

  if (!visible) return null;

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        marginTop: -2,
        ...bannerSurface(awaiting ? "rgb(191, 117, 22)" : "rgb(195, 75, 54)"),
        padding: "12px 24px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? 10 : 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
      }}
    >
      <span style={{ fontWeight: 500, textAlign: "center", width: isMobile ? "100%" : undefined }}>
        {awaiting
          ? "Your billing cycle ended while your account was paused - please reactivate to continue."
          : "Services are paused — the required credit is missing from one of your videos."}
      </span>
      <button
        // Awaiting reactivation: nothing is left to fix, so go straight to the
        // prefilled checkout rather than routing to the claims page and making
        // the user click through a modal that has one button. Credit-missing
        // still routes to the modal, which is where the fixing happens.
        onClick={() => (awaiting ? void openReactivation() : navigate("/account/claims?fix=1"))}
        style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "6px 14px", borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none", color: "#2a1d14",
          fontFamily: '"Raleway", sans-serif', fontSize: "0.8125rem", fontWeight: 600,
          cursor: "pointer", whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)", transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          {awaiting ? (
            <>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </>
          ) : (
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          )}
        </svg>
        {awaiting ? "Reactivate" : videoCount > 1 ? "Fix issues" : "Fix issue"}
      </button>
    </div>
  );
};

export default WhitelistingSuspendedGlobalBanner;
